import { CloseOutlined } from "@ant-design/icons"
import { Checkbox, Empty } from "antd"
import { SearchIcon } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import BaseInput from "~/components/base-input"
import { OSLoader } from "~/components/base-loader"
import { useSearchKnowledgeLibrary } from "~/hooks/library"

import {
  FileSource,
  getMimeFromName,
  ISelectedFile,
  MEDIA_EXTS,
  normalizeFileId
} from "./types"

interface Props {
  accept: string
  currentlyAttached?: string[]
  selected: ISelectedFile[]
  setSelected: (next: (prev: ISelectedFile[]) => ISelectedFile[]) => void
  maxFiles: number
  showTemplateTag?: boolean
  selectableExtensions?: string[]
}

export function KbTab(props: Props) {
  const {
    accept,
    currentlyAttached,
    selected,
    setSelected,
    maxFiles,
    showTemplateTag,
    selectableExtensions
  } = props
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const { t } = useTranslation()
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(id)
  }, [search])
  const { data, isLoading } = useSearchKnowledgeLibrary({
    search: debouncedSearch,
    tagIds: []
  })

  const acceptList = useMemo(
    () => accept.split(",").map((s) => s.trim().toLowerCase()),
    [accept]
  )

  const files = useMemo(() => {
    // Backend returns { sources: [...], total, offset, size }
    // Each item: { id, name, source_type, description, link, has_file, ... }
    // Virtual sources (qna, scrape_*) have no backing S3 file; filter them out
    // since picking them and forwarding to PPT/agent backends would fail.
    // ENG-979: prefer the `has_file` discriminator (BE now sends it); fall back
    // to `!!s3_path` so this works before/after the backend rollout. Once
    // s3_path is dropped (P3) `has_file` is the sole signal.
    const all = (data?.sources ?? data?.files ?? []) as Array<{
      id: string
      name: string
      link?: string
      has_file?: boolean
      s3_path?: string | null
      source_type?: string
      description?: string
    }>
    return all.filter((f) => {
      if (!(f.has_file ?? !!f.s3_path)) return false
      const ext = (f.name?.split(".").pop() ?? "").toLowerCase()
      return acceptList.includes("." + ext)
    })
  }, [data, acceptList])

  const toggle = (f: (typeof files)[number]) => {
    const ext = (f.name?.split(".").pop() ?? "").toLowerCase()
    // KB chunk ids are numeric and don't carry the composite shape, but
    // normalize for symmetry with the other tabs — keeps dedup behavior
    // consistent if a future KB source ever returns a piped id.
    const normalizedFileId = normalizeFileId(f.id)
    const sf: ISelectedFile = {
      source: "kb" as FileSource,
      file_id: normalizedFileId,
      s3_path: f.s3_path ?? "",
      file_name: f.name,
      mime_type: getMimeFromName(f.name),
      is_media: MEDIA_EXTS.has(ext)
    }
    setSelected((prev) => {
      if (prev.find((p) => normalizeFileId(p.file_id) === normalizedFileId)) {
        return prev.filter(
          (p) => normalizeFileId(p.file_id) !== normalizedFileId
        )
      }
      if (prev.length >= maxFiles) return prev
      return [...prev, sf]
    })
  }

  return (
    <div className="flex flex-col gap-2">
      <BaseInput
        placeholder="Search knowledge library…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="!h-10 [&_input]:!h-10 [&_input]:!text-left [&_input]:!pt-3 [&_input]:!pb-3 [&_input]:!box-border [&_input]:!leading-normal [&.ant-input-affix-wrapper]:!flex [&.ant-input-affix-wrapper]:!items-center"
        allowClear={{
          clearIcon: (
            <CloseOutlined className="w-4 h-4 dark:text-dark-6 text-dark-2" />
          )
        }}
        onClear={() => setSearch("")}
        suffix={
          <SearchIcon
            className="w-4 h-4 dark:text-dark-6 text-dark-2"
            strokeWidth={1}
          />
        }
      />
      {isLoading ? (
        <div className="w-full min-h-[200px] flex items-center justify-center border border-light-border dark:border-dark-border rounded-lg bg-white dark:bg-dark-1">
          <OSLoader />
        </div>
      ) : files.length === 0 ? (
        <div className="w-full min-h-[200px] flex items-center justify-center border border-light-border dark:border-dark-border rounded-lg bg-white dark:bg-dark-1">
          <Empty
            description={<p className="text-xs">No files found.</p>}
            className="mt-0"
          />
        </div>
      ) : (
        <ul className="max-h-96 overflow-auto border border-light-border dark:border-dark-border rounded-lg bg-white dark:bg-dark-1">
          {files.map((f) => {
            const fNorm = normalizeFileId(f.id)
            const checked = !!selected.find(
              (p) => normalizeFileId(p.file_id) === fNorm
            )
            const disabledByCap = !checked && selected.length >= maxFiles
            const disabledByAttached = currentlyAttached?.includes(f.id)
            const rowExt = (f.name?.split(".").pop() ?? "").toLowerCase()
            const disabledByType =
              !!selectableExtensions &&
              selectableExtensions.length > 0 &&
              !selectableExtensions.includes(rowExt)
            return (
              <li
                key={f.id}
                className={
                  "flex items-center gap-2 py-2 px-2.5 border-b-[0.5px] border-light-border dark:border-dark-border last:border-b-0 " +
                  (disabledByType
                    ? "cursor-not-allowed opacity-50"
                    : "hover:bg-gray-50 dark:hover:bg-dark-2 cursor-pointer")
                }
                onClick={() =>
                  !disabledByCap &&
                  !disabledByAttached &&
                  !disabledByType &&
                  toggle(f)
                }
              >
                <Checkbox
                  checked={checked}
                  disabled={
                    disabledByCap || !!disabledByAttached || disabledByType
                  }
                  onChange={() => toggle(f)}
                  onClick={(e) => e.stopPropagation()}
                  className="flex-shrink-0"
                />
                <p className="text-xs dark:text-dark-6 text-dark-2 truncate">
                  {f.name}
                </p>
                {showTemplateTag && checked ? (
                  <span className="ml-auto text-[11px] font-semibold tracking-wide uppercase text-primary dark:text-primary-light shrink-0">
                    {t("ask_os.label_template_tag")}
                  </span>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
